import React from 'react';
import Icon from '../../../components/AppIcon';

const LeadQualityScoringPanel = ({ channelData }) => {
  // Calculate quality scores based on conversion rate and ROI
  const getQualityScore = (channel) => {
    const conversionWeight = 0.6;
    const roiWeight = 0.4;
    const normalizedConversion = Math.min(channel?.conversionRate / 40, 1); // Normalize to 0-1
    const normalizedROI = Math.min(channel?.roi / 500, 1); // Normalize to 0-1
    return Math.round((normalizedConversion * conversionWeight + normalizedROI * roiWeight) * 100);
  };

  const scoredChannels = channelData?.map(channel => ({
    ...channel,
    qualityScore: getQualityScore(channel)
  }))?.sort((a, b) => b?.qualityScore - a?.qualityScore);

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score) => {
    if (score >= 80) return 'bg-green-100';
    if (score >= 60) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
          <Icon name="Award" className="w-5 h-5 text-blue-600" />
          Lead Quality Scoring
        </h2>
        <p className="text-sm text-gray-600 mt-1">Real-time conversion likelihood analysis</p>
      </div>

      {/* Quality Score Rankings */}
      <div className="space-y-3 mb-6">
        {scoredChannels?.map((channel, index) => (
          <div key={channel?.id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-gray-400">#{index + 1}</span>
                <span className="font-medium text-gray-900">{channel?.channel}</span>
              </div>
              <div className={`text-2xl font-bold ${getScoreColor(channel?.qualityScore)}`}>
                {channel?.qualityScore}
              </div>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-2">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  channel?.qualityScore >= 80 ? 'bg-green-500' :
                  channel?.qualityScore >= 60 ? 'bg-yellow-500': 'bg-red-500'
                }`}
                style={{ width: `${channel?.qualityScore}%` }}
              />
            </div>
            <div className="flex items-center justify-between text-xs text-gray-600">
              <span>Conv: {channel?.conversionRate}%</span>
              <span>ROI: {channel?.roi}%</span>
              <span className={`px-2 py-1 rounded font-medium ${getScoreBgColor(channel?.qualityScore)} ${getScoreColor(channel?.qualityScore)}`}>
                {channel?.quality} quality
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Automated Routing Recommendations */}
      <div className="bg-purple-50 rounded-lg p-4 mb-4">
        <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
          <Icon name="GitBranch" className="w-4 h-4 text-purple-600" />
          Automated Routing Recommendations
        </h3>
        <div className="space-y-2 text-sm text-gray-700">
          <div className="flex items-start gap-2">
            <Icon name="ArrowRight" className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
            <span><strong>High Quality (80+):</strong> Route to senior sales representatives</span>
          </div>
          <div className="flex items-start gap-2">
            <Icon name="ArrowRight" className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
            <span><strong>Medium Quality (60-79):</strong> Nurture campaign with targeted content</span>
          </div>
          <div className="flex items-start gap-2">
            <Icon name="ArrowRight" className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
            <span><strong>Low Quality (&lt;60):</strong> Automated email sequence with qualification</span>
          </div>
        </div>
      </div>

      {/* Conversion Likelihood Indicators */}
      <div className="border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-gray-900 mb-3">Conversion Likelihood Factors</h3>
        <div className="space-y-2 text-xs text-gray-600">
          <div className="flex items-center justify-between">
            <span>Source credibility</span>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5]?.map(i => (
                <Icon key={i} name="Star" className={`w-3 h-3 ${i <= 4 ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} />
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span>Engagement level</span>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5]?.map(i => (
                <Icon key={i} name="Star" className={`w-3 h-3 ${i <= 3 ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} />
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span>Historical performance</span>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5]?.map(i => (
                <Icon key={i} name="Star" className={`w-3 h-3 ${i <= 5 ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadQualityScoringPanel;